import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PatientBookingFormModal } from './PatientBookingFormModal';
import { getPatientBookingColumns } from './patientBookingColumns';
import { usePatientBookings } from '../../hooks/usePatientBookings';
import { usePatients } from '../../hooks/usePatients';
import type { PatientBookingRequest, PatientBookingResponse } from '../../api/generated';
import {
  BookingListSection,
  DeleteBookingModal,
  formatRecurrenceSummary,
  isRecurringTime,
  resolveEntityNames,
  useBookingList
} from '../Bookings/shared';
import { PATIENT_BOOKING_TYPE_LABEL_KEYS } from './patientBookingConstants';

const EMPTY = '—';
const TABLE_MIN_WIDTH = 820;
const KEY_PREFIX = 'patient-booking';

export const PatientBookings = () => {
  const { t } = useTranslation();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const {
    bookings,
    isLoading,
    loadError,
    locationId,
    refresh,
    createBooking,
    updateBooking,
    deleteBooking
  } = usePatientBookings(routeLocationId ?? null);
  const { patients, loadError: patientsLoadError, refresh: refreshPatients } = usePatients(
    routeLocationId ?? null
  );

  const list = useBookingList<PatientBookingResponse, PatientBookingRequest>({
    keyPrefix: KEY_PREFIX,
    createBooking,
    updateBooking,
    deleteBooking,
    isRecurringPayload: (payload) => isRecurringTime(payload.time)
  });

  const patientNameById = useMemo(
    () => new Map(patients.map((patient) => [patient.id, patient.name])),
    [patients]
  );

  const columns = useMemo(
    () =>
      getPatientBookingColumns(t, {
        patientNameById,
        onEdit: list.openEdit,
        onDelete: list.requestDelete
      }),
    [t, patientNameById, list.openEdit, list.requestDelete]
  );

  // Who the booking is for, for the delete confirmation: the "all …" mode label, or the resolved
  // names (falling back to a dash if every referenced patient has since been removed).
  const patientLabelFor = (booking: PatientBookingResponse): string => {
    const allLabelKey = PATIENT_BOOKING_TYPE_LABEL_KEYS[booking.patientBookingType];
    if (allLabelKey) return t(allLabelKey);
    const names = resolveEntityNames(booking.patientIds, patientNameById);
    return names.length > 0 ? names.join(', ') : EMPTY;
  };

  // Either the bookings or the patient list failing to load leaves the page unusable, so surface
  // whichever failed and let a single retry refetch both.
  const errorMessage = loadError ?? patientsLoadError;
  const handleRetry = () => {
    refresh();
    refreshPatients().catch(() => {
      // patient load errors already surface through patientsLoadError
    });
  };

  return (
    <BookingListSection
      keyPrefix={KEY_PREFIX}
      isLoading={isLoading}
      errorMessage={errorMessage}
      locationId={locationId}
      columns={columns}
      bookings={bookings}
      minWidth={TABLE_MIN_WIDTH}
      onAdd={list.openCreate}
      onRetry={handleRetry}
    >
      {list.isFormOpen && (
        <PatientBookingFormModal
          booking={list.editingBooking}
          patients={patients}
          onSave={(payload) => {
            list.handleSave(payload);
          }}
          onClose={list.closeForm}
        />
      )}

      {list.deleteTarget && (
        <DeleteBookingModal
          titleKey="patient-booking-delete-title"
          confirmKey="patient-booking-delete-confirm"
          messageKey="patient-booking-delete-message"
          recurringMessageKey="patient-booking-delete-recurring-message"
          name={patientLabelFor(list.deleteTarget)}
          recurrenceSummary={
            isRecurringTime(list.deleteTarget.time)
              ? formatRecurrenceSummary(t, list.deleteTarget.time)
              : undefined
          }
          onConfirm={() => {
            list.handleConfirmDelete();
          }}
          onClose={list.cancelDelete}
        />
      )}
    </BookingListSection>
  );
};
